import { useEffect, useState } from 'react';
import type { SafetyHistoryResponse, SafetyHistoryRow } from '../../../types/safetySystem';
import { Field, inputClass, str } from './unitFields';

/**
 * Pieces shared by the Safety drawers (Unit / Ticket / Maintenance / Incident): the
 * server-error-to-text rule, the read-only stamp field, the datetime formatter, the
 * drag-to-resize handle, the "Discard changes?" footer and the record history tab.
 */

/**
 * Server error detail -> display string. The Safety routers return a plain string for
 * most rejections and an OBJECT for the structured ones: a 409 dupe
 * (`{ message, dupes: [...] }`) and the incident closing checklist 422
 * (`{ message, failures: [...] }`). The message is what shows in the alert; the drawers
 * render `dupes` / `failures` themselves. `withDupeUnits` appends the duplicate rows'
 * unit numbers (the Unit drawer's truck VIN dupe).
 */
export const errorText = (e: any, { withDupeUnits = false }: { withDupeUnits?: boolean } = {}): string => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && typeof d.message === 'string') {
    if (withDupeUnits && Array.isArray(d.dupes)) {
      const units = d.dupes.map((x: any) => x?.unitnumber).filter(Boolean);
      if (units.length) return `${d.message} — already on unit ${units.join(', ')}`;
    }
    return d.message;
  }
  return e?.message || 'Save failed.';
};

/** A labelled, disabled input for record stamps (created / changed / status). */
export const ReadOnly = ({ label, value, className }: { label: string; value: unknown; className?: string }) => (
  <Field label={label} className={className}>
    <input aria-label={label} className={inputClass} value={str(value)} readOnly disabled />
  </Field>
);

/** DB datetime -> `MM/DD/YYYY HH:MM`; a date-only value -> `MM/DD/YYYY`; zero dates -> ''; anything else as-is. */
export const fmtDateTime = (v: unknown): string => {
  const s = str(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
  if (m) return m[1] === '0000' ? '' : `${m[2]}/${m[3]}/${m[1]} ${m[4]}:${m[5]}`;
  const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (d) return d[1] === '0000' ? '' : `${d[2]}/${d[3]}/${d[1]}`;
  return s === '0' ? '' : s;
};

/**
 * Same drag handle as the Pro detail / metric drill-down drawers: a fixed strip tracking
 * the panel's left edge; drag to resize, double-click to snap flush to the app nav.
 * Takes the `useDrawerResize` outputs.
 */
export const ResizeHandle = ({ width, onHandleMouseDown, onHandleDoubleClick }: {
  width: number; onHandleMouseDown: (e: React.MouseEvent) => void; onHandleDoubleClick: (e: React.MouseEvent) => void;
}) => (
  <div
    role="separator"
    aria-orientation="vertical"
    aria-label="Resize panel"
    title="Drag to resize · double-click to snap to nav"
    className="fixed top-0 z-[101] h-full w-1 cursor-ew-resize bg-slate-200 transition-colors hover:bg-blue-400 active:bg-blue-500 pointer-events-auto dark:bg-gray-700 dark:hover:bg-blue-500 max-sm:hidden"
    style={{ right: `${width - 4}px` }}
    onMouseDown={onHandleMouseDown}
    onDoubleClick={onHandleDoubleClick}
  />
);

/** Footer shown while a dirty drawer is being closed: Discard / Keep editing. */
export const DiscardFooter = ({ onDiscard, onKeep }: { onDiscard: () => void; onKeep: () => void }) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <span className="text-sm font-medium text-danger-fg">Discard changes?</span>
    <div className="flex gap-2">
      <button type="button" onClick={onDiscard} className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700">Discard</button>
      <button type="button" onClick={onKeep} className="rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight">Keep editing</button>
    </div>
  </div>
);

const th = 'px-1.5 py-1 text-left text-xs font-semibold uppercase tracking-wide text-muted';

export interface SafetyHistoryTabProps {
  recno: number;
  /** The record type's history reader, e.g. `safetySystemService.getTicketHistory` (a stable reference). */
  fetch: (recno: number, signal?: AbortSignal) => Promise<SafetyHistoryResponse>;
  /** `dbtable` -> display label ("ticket" -> "Ticket"). Unknown tables show their raw name. */
  labels: Record<string, string>;
  /**
   * When set, a "Source" column shows which history table each row came from
   * (`row.source` -> label). The Unit drawer merges unit_/driver_/truck_history.
   */
  sourceLabels?: Record<string, string>;
  emptyText?: string;
}

/** Read-only `<record>_history` reader (newest first) shared by the Unit / Ticket / Maintenance / Incident drawers. */
export function SafetyHistoryTab({ recno, fetch, labels, sourceLabels, emptyText = 'No history for this record.' }: SafetyHistoryTabProps) {
  const [rows, setRows] = useState<SafetyHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetch(recno, controller.signal)
      .then((res) => { if (!controller.signal.aborted) setRows(res.data ?? []); })
      .catch((e) => { if (!controller.signal.aborted) setError(e?.response?.data?.detail || 'Unable to load history.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [recno, fetch]);

  if (loading) return <div className="py-6 text-center text-sm text-muted">Loading history…</div>;
  if (error) return <div role="alert" className="rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{error}</div>;
  if (!rows.length) return <div className="py-6 text-center text-sm text-muted">{emptyText}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className={th}>When</th><th className={th}>User</th>
            {sourceLabels && <th className={th}>Source</th>}
            <th className={th}>Table</th><th className={th}>Row</th><th className={th}>Column</th><th className={th}>Old</th><th className={th}>New</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => (
            // Merged readers can repeat a recno across source tables: key on both.
            <tr key={`${h.source ?? ''}-${h.recno}`} className="border-t border-rule">
              <td className="whitespace-nowrap px-1.5 py-1">{fmtDateTime(h.datetime)}</td>
              <td className="px-1.5 py-1">{h.userid}</td>
              {sourceLabels && <td className="px-1.5 py-1">{sourceLabels[h.source ?? ''] ?? h.source ?? ''}</td>}
              <td className="px-1.5 py-1">{labels[h.dbtable] ?? h.dbtable}</td>
              <td className="px-1.5 py-1 text-muted">{h.otherid && h.otherid !== '0' ? h.otherid : ''}</td>
              <td className="px-1.5 py-1">{h.colname}</td>
              <td className="max-w-[16rem] break-words px-1.5 py-1 text-muted">{str(h.oldval)}</td>
              <td className="max-w-[16rem] break-words px-1.5 py-1">{str(h.newval)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
